import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Scale } from 'lucide-react';

interface AppealStrategy {
  recommendedStrategy: string;
  successProbability: number;
  reasoning: string;
  requiredEvidence: string[];
  legalGrounds: string[];
}

interface AppealStrategyCardProps {
  strategy: AppealStrategy;
}

const AppealStrategyCard: React.FC<AppealStrategyCardProps> = ({ strategy }) => {
  // Determine color coding based on success probability
  const getProbabilityColor = (probability: number) => {
    if (probability >= 60) return { text: 'text-green-600', bg: 'bg-green-500', label: 'High Chance' };
    if (probability >= 40) return { text: 'text-amber-600', bg: 'bg-amber-500', label: 'Moderate Chance' };
    return { text: 'text-red-600', bg: 'bg-red-500', label: 'Low Chance' };
  };

  const getProbabilityIcon = (probability: number) => {
    if (probability >= 60) return <CheckCircle2 size={20} className="text-green-600" />;
    if (probability >= 40) return <AlertTriangle size={20} className="text-amber-600" />;
    return <XCircle size={20} className="text-red-600" />;
  };

  const color = getProbabilityColor(strategy.successProbability);

  return (
    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <Scale size={18} className="text-purple-600 dark:text-purple-400" />
        <h4 className="text-sm font-bold text-purple-900 dark:text-purple-100">
          🎯 AI Success Prediction
        </h4>
      </div>

      {/* Success Probability Gauge */}
      <div className="flex items-center gap-4 mb-3">
        <div className="flex-1">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs font-semibold text-purple-900 dark:text-purple-100">Success Probability</span>
            <div className="flex items-center gap-2">
              {getProbabilityIcon(strategy.successProbability)}
              <span className={`text-lg font-bold ${color.text}`}>
                {strategy.successProbability}%
              </span>
            </div>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className={`${color.bg} h-2 rounded-full transition-all duration-500`}
              style={{ width: `${strategy.successProbability}%` }}
            ></div>
          </div>
          <p className="text-[10px] text-purple-600 dark:text-purple-400 mt-1">
            {color.label}
          </p>
        </div>
      </div>

      {/* Recommended Strategy */}
      <div className="mb-3">
        <p className="text-xs text-purple-700 dark:text-purple-300 mb-1">
          <strong>Recommended Strategy:</strong> {strategy.recommendedStrategy}
        </p>
        <p className="text-xs text-purple-600 dark:text-purple-400 italic leading-relaxed">
          {strategy.reasoning}
        </p>
      </div>

      {/* Required Evidence */}
      {strategy.requiredEvidence && strategy.requiredEvidence.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-purple-900 dark:text-purple-100 mb-2">Required Evidence:</p>
          <ul className="space-y-1">
            {strategy.requiredEvidence.map((item, idx) => (
              <li key={idx} className="text-xs text-purple-700 dark:text-purple-300 flex items-start gap-2">
                <span className="text-purple-500 mt-0.5">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Legal Grounds */}
      {strategy.legalGrounds && strategy.legalGrounds.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-purple-900 dark:text-purple-100 mb-2">Legal Grounds:</p>
          <div className="flex flex-wrap gap-1">
            {strategy.legalGrounds.map((ground, idx) => (
              <span
                key={idx}
                className="text-[10px] bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full font-medium"
              >
                {ground}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppealStrategyCard;
